import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileCheck, AlertTriangle, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, formatApiError, API } from "@/lib/api";

const empty = {
  name: "", mc_number: "", dot_number: "",
  contact_name: "", contact_phone: "", contact_email: "",
  address: "", city: "", state: "", zip: "",
  equipment_types: "", notes: "",
  coi_expiry_date: "",
};

export default function CarrierFormDialog({ open, onOpenChange, carrier, onSaved }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [coiBusy, setCoiBusy] = useState(false);
  const [savedCarrier, setSavedCarrier] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      const initial = carrier ? { ...empty, ...carrier, coi_expiry_date: carrier.coi_expiry_date?.slice(0, 10) || "" } : empty;
      setForm(initial);
      setSavedCarrier(carrier || null);
    }
  }, [open, carrier]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.coi_expiry_date === "") delete payload.coi_expiry_date;
      let saved;
      if (savedCarrier?.id) {
        saved = (await api.patch(`/carriers/${savedCarrier.id}`, payload)).data;
        toast.success("Carrier updated");
      } else {
        saved = (await api.post("/carriers", payload)).data;
        toast.success("Carrier added");
      }
      setSavedCarrier(saved);
      setForm({ ...empty, ...saved, coi_expiry_date: saved.coi_expiry_date?.slice(0, 10) || "" });
      onSaved?.();
      // Keep dialog open so user can upload COI right after creating
      if (!savedCarrier?.id && !saved.coi_file) {
        // opened after first save so user can upload COI
      } else {
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const uploadCoi = async (file) => {
    if (!file || !savedCarrier?.id) {
      toast.error("Save the carrier first, then upload the COI.");
      return;
    }
    setCoiBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/carriers/${savedCarrier.id}/coi`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSavedCarrier(data);
      setForm((f) => ({ ...f, coi_expiry_date: data.coi_expiry_date?.slice(0, 10) || "" }));
      if (data.coi_parse_source === "auto") {
        toast.success(`COI uploaded. Expiry auto-detected: ${data.coi_expiry_date}`);
      } else {
        toast.warning("COI uploaded but expiry could not be auto-detected. Please enter it manually below.");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setCoiBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteCoi = async () => {
    if (!savedCarrier?.id) return;
    if (!window.confirm("Remove the current COI? Carrier will be marked inactive.")) return;
    try {
      const { data } = await api.delete(`/carriers/${savedCarrier.id}/coi`);
      setSavedCarrier(data);
      setForm((f) => ({ ...f, coi_expiry_date: "" }));
      toast.success("COI removed");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const backendBase = API.replace(/\/api$/, "");
  const coiUrl = savedCarrier?.coi_file?.url ? `${backendBase}${savedCarrier.coi_file.url}` : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sharp max-w-2xl max-h-[90vh] overflow-y-auto border-zinc-950">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight">
            {carrier ? "Edit carrier" : "New carrier"}
          </DialogTitle>
          <p className="text-sm text-zinc-600">Saved carriers are available to pre-fill rate confirmations.</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="mono-label">Carrier name *</label>
              <Input required value={form.name} onChange={upd("name")} className="sharp mt-1 apex-input" data-testid="carrier-name" />
            </div>
            <div>
              <label className="mono-label">MC # *</label>
              <Input required value={form.mc_number} onChange={upd("mc_number")} className="sharp mt-1 apex-input font-mono" data-testid="carrier-mc" />
            </div>
            <div>
              <label className="mono-label">DOT #</label>
              <Input value={form.dot_number} onChange={upd("dot_number")} className="sharp mt-1 apex-input font-mono" />
            </div>
            <div>
              <label className="mono-label">Equipment types</label>
              <Input value={form.equipment_types} onChange={upd("equipment_types")}
                placeholder="Dry Van, Reefer, Flatbed..." className="sharp mt-1 apex-input" />
            </div>
          </div>

          <div className="border-t border-zinc-200 pt-4">
            <div className="mono-label mb-3">CONTACT</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="mono-label">Name</label>
                <Input value={form.contact_name} onChange={upd("contact_name")} className="sharp mt-1 apex-input" />
              </div>
              <div>
                <label className="mono-label">Phone</label>
                <Input value={form.contact_phone} onChange={upd("contact_phone")} className="sharp mt-1 apex-input" />
              </div>
              <div>
                <label className="mono-label">Email</label>
                <Input type="email" value={form.contact_email} onChange={upd("contact_email")} className="sharp mt-1 apex-input" />
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-200 pt-4">
            <div className="mono-label mb-3">ADDRESS</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="mono-label">Street</label>
                <Input value={form.address} onChange={upd("address")} className="sharp mt-1 apex-input" />
              </div>
              <div>
                <label className="mono-label">City</label>
                <Input value={form.city} onChange={upd("city")} className="sharp mt-1 apex-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mono-label">State</label>
                  <Input value={form.state} onChange={upd("state")} className="sharp mt-1 apex-input" />
                </div>
                <div>
                  <label className="mono-label">Zip</label>
                  <Input value={form.zip} onChange={upd("zip")} className="sharp mt-1 apex-input" />
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="mono-label">CERTIFICATE OF INSURANCE (COI)</div>
              {savedCarrier && (
                savedCarrier.is_active
                  ? <span className="mono-label !text-emerald-800 inline-flex items-center gap-1"><FileCheck className="h-3 w-3" /> ACTIVE</span>
                  : <span className="mono-label !text-rose-800 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> INACTIVE</span>
              )}
            </div>
            {!savedCarrier?.id ? (
              <p className="text-xs text-zinc-500">Save the carrier first, then upload the COI.</p>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="mono-label">Expiry date (auto-detected on upload)</label>
                    <Input type="date" value={form.coi_expiry_date} onChange={upd("coi_expiry_date")}
                      className="sharp mt-1 apex-input font-mono" data-testid="coi-expiry" />
                    <div className="text-[10px] mono-label mt-1">
                      {savedCarrier.coi_days_to_expiry != null && (
                        savedCarrier.coi_days_to_expiry < 0
                          ? <span className="!text-rose-800">EXPIRED {Math.abs(savedCarrier.coi_days_to_expiry)} DAYS AGO</span>
                          : savedCarrier.coi_days_to_expiry <= 30
                            ? <span className="!text-amber-800">EXPIRES IN {savedCarrier.coi_days_to_expiry} DAYS</span>
                            : <span className="!text-emerald-800">VALID · {savedCarrier.coi_days_to_expiry} DAYS LEFT</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-end">
                    <Button type="button" onClick={() => fileRef.current?.click()} disabled={coiBusy}
                      className="sharp bg-zinc-950 hover:bg-zinc-800 w-full h-10" data-testid="coi-upload-btn">
                      <Upload className="h-4 w-4 mr-2" /> {coiBusy ? "Uploading..." : (savedCarrier.coi_file ? "Replace COI" : "Upload COI")}
                    </Button>
                    <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={(e) => uploadCoi(e.target.files?.[0])} className="hidden" data-testid="coi-file-input" />
                  </div>
                </div>
                {savedCarrier.coi_file && (
                  <div className="flex items-center justify-between border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs">
                    <a href={coiUrl} target="_blank" rel="noreferrer" className="text-[#0056B3] underline font-mono truncate max-w-xs"
                       data-testid="coi-file-link">
                      {savedCarrier.coi_file.original_name}
                    </a>
                    <button type="button" onClick={deleteCoi} className="text-rose-700 inline-flex items-center gap-1" data-testid="coi-delete">
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="mono-label">Notes</label>
            <Textarea rows={3} value={form.notes} onChange={upd("notes")} className="sharp mt-1 apex-input" />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="sharp border-zinc-300" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="carrier-submit">
              {busy ? "Saving..." : (carrier ? "Save changes" : "Add carrier")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
